import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Minus, Plus, ShoppingCart, CheckCircle, Phone, Tag, Mail, User, X, Ticket, CreditCard, Store, Loader2, Clock } from "lucide-react";
import FunFactsCarousel from "@/components/FunFactsCarousel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/contexts/CartContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { formatPrice, BRAND_COLORS } from "@/lib/menuData";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import pushService from "@/services/pushNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_foodapp-24/artifacts/qn823x2x_PHOTO-2026-02-06-16-32-00.jpg";

// Pizza pricing: 1st @ $11.99, 2nd @ $10.99, 3rd @ $11.99, 4th @ $10.99, etc.
// Limited Edition pizzas are excluded from deals (always full price)
const PIZZA_PRICING = {
  firstPrice: 11.99,  // Odd pizzas (1st, 3rd, 5th...)
  secondPrice: 10.99, // Even pizzas (2nd, 4th, 6th...)
  extraCheese: 2.49,  // Always charged separately
};

// Stripe processing fees (only applies when paying online)
const STRIPE_FEES = {
  processingPercent: 0.03, // 3% of subtotal
  serviceFee: 0.30,        // Fixed $0.30 per transaction
};

export default function CartDrawer({ open, onClose, isInStoreMode = false }) {
  const { items, removeItem, updateQuantity, clearCart, total, itemCount } = useCart();
  const { customer, isAuthenticated, loginWithGoogle, guestCheckout, processingAuth } = useCustomerAuth();
  
  // Checkout mode: 'select' | 'guest' | 'authenticated' | 'instore'
  const [checkoutMode, setCheckoutMode] = useState(isInStoreMode ? 'instore' : 'select');
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null); // 'waiting' | 'accepted' | 'declined'
  const [declineReason, setDeclineReason] = useState(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);

  // Pizza loyalty (Phase 14 Batch C)
  const [loyalty, setLoyalty] = useState({ enabled: true, count: 0, target: 6 });
  
  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState("");

  // Payment state - in-store mode always uses "at_counter"
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [storePaymentEnabled, setStorePaymentEnabled] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState(isInStoreMode ? "at_counter" : "pickup");
  const [processingPayment, setProcessingPayment] = useState(false);

  // Update checkout mode when isInStoreMode changes
  useEffect(() => {
    if (isInStoreMode) {
      setCheckoutMode('instore');
      setPaymentMethod('at_counter');
    }
  }, [isInStoreMode]);

  // Auto-fill customer data if authenticated (not in in-store mode)
  useMemo(() => {
    if (!isInStoreMode && isAuthenticated && customer) {
      setCustomerName(customer.name || "");
      setCustomerEmail(customer.email || "");
      setPhoneNumber(customer.phone || "");
      setCheckoutMode('authenticated');
    }
  }, [isAuthenticated, customer, isInStoreMode]);

  // Check if payment methods are enabled (not needed in in-store mode)
  useEffect(() => {
    if (isInStoreMode) {
      setPaymentsEnabled(false);
      setStorePaymentEnabled(false);
      return;
    }
    const checkPaymentsEnabled = async () => {
      try {
        const res = await axios.get(`${API}/payments/enabled`);
        setPaymentsEnabled(res.data.enabled);
        setStorePaymentEnabled(res.data.store_enabled !== false);
        // Set default payment method based on availability
        if (res.data.store_enabled !== false) {
          setPaymentMethod("pickup");
        } else if (res.data.enabled) {
          setPaymentMethod("online");
        }
      } catch (e) {
        console.error("Failed to check payment status:", e);
        setPaymentsEnabled(false);
        setStorePaymentEnabled(true);
      }
    };
    checkPaymentsEnabled();
  }, [isInStoreMode]);

  // Phase 14 Batch C: Fetch pizza loyalty progress when phone number is entered
  useEffect(() => {
    const digits = (phoneNumber || "").replace(/\D/g, "");
    if (!open || isInStoreMode || digits.length < 10) {
      return;
    }
    let cancelled = false;
    const fetchLoyalty = async () => {
      try {
        const res = await axios.get(`${API}/loyalty/pizza/${digits}`);
        if (!cancelled) setLoyalty(res.data);
      } catch (_) {
        /* no-op */
      }
    };
    fetchLoyalty();
    return () => { cancelled = true; };
  }, [phoneNumber, open, isInStoreMode]);

  // Phase 14 Batch C: Poll order status after placement (waiting → accepted/declined)
  useEffect(() => {
    if (!orderPlaced || !orderId || isInStoreMode || orderStatus === "accepted" || orderStatus === "declined") {
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/orders/status/${orderId}`);
        if (cancelled) return;
        const st = res.data.status;
        if (st === "declined") {
          setOrderStatus("declined");
          setDeclineReason(res.data.decline_reason || null);
        } else if (["preparing", "accepted", "ready", "completed", "picked_up"].includes(st)) {
          setOrderStatus("accepted");
        } else {
          setOrderStatus("waiting");
        }
      } catch (_) {
        /* keep polling */
      }
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [orderPlaced, orderId, orderStatus, isInStoreMode]);

  // Calculate pizza pricing with alternating prices for CLASSIC pizzas only
  // Specialty pizzas are always $12.99 (no alternating deal)
  const pizzaPricing = useMemo(() => {
    // Specialty pizzas - always charged at their price ($12.99)
    const specialtyPizzaItems = items.filter(item => 
      item.concept === "pizza" && 
      item.category !== "pizza_addon" &&
      (item.category === "specialty_pizza" || item.customizations?.isSpecialty)
    );
    const specialtyPizzaTotal = specialtyPizzaItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const specialtyCount = specialtyPizzaItems.reduce((sum, item) => sum + item.quantity, 0);
    
    // Classic/Regular pizzas (eligible for alternating deal) - includes custom_pizza
    const classicPizzaItems = items.filter(item => 
      item.concept === "pizza" && 
      item.category !== "pizza_addon" && 
      item.category !== "specialty_pizza" &&
      !item.customizations?.isSpecialty
    );
    const classicPizzaCount = classicPizzaItems.reduce((sum, item) => sum + item.quantity, 0);
    
    // Calculate total for alternating pricing on classic pizzas only
    let classicPizzaTotal = 0;
    for (let i = 1; i <= classicPizzaCount; i++) {
      // Odd positions = $11.99, Even positions = $10.99
      classicPizzaTotal += (i % 2 === 1) ? PIZZA_PRICING.firstPrice : PIZZA_PRICING.secondPrice;
    }
    
    // Original price if all classic pizzas were $11.99
    const originalPrice = classicPizzaCount * PIZZA_PRICING.firstPrice;
    const savings = originalPrice - classicPizzaTotal;

    // For loyalty reward projection — cheapest pizza in cart is the free one
    const allPizzas = [...specialtyPizzaItems, ...classicPizzaItems];
    const pizzaLineUnits = [];
    for (const it of allPizzas) {
      // For classic pizzas we already alternated pricing; use the per-item .price for a
      // simple, conservative estimate. Server is the source of truth.
      for (let q = 0; q < it.quantity; q++) pizzaLineUnits.push(it.price || 0);
    }
    pizzaLineUnits.sort((a, b) => a - b);
    const minPizzaPrice = pizzaLineUnits[0] || 0;

    return {
      totalPizzas: classicPizzaCount,
      specialtyCount,
      specialtyPizzaTotal,
      classicPizzaTotal,
      pizzaTotal: classicPizzaTotal + specialtyPizzaTotal,
      savings,
      hasSavings: savings > 0,
      minPizzaPrice,
      totalPizzaUnits: pizzaLineUnits.length,
    };
  }, [items]);

  // Tax rate constant (10%)
  const TAX_RATE = 0.10;

  // Calculate adjusted total with pizza pricing, promo discounts, loyalty reward, and Stripe fees
  const { subtotal, discountAmount, loyaltyDiscount, subtotalAfterDiscount, serviceFee, subtotalWithFees, taxAmount, grandTotal, projectedRewards } = useMemo(() => {
    // Get non-pizza total
    const nonPizzaTotal = items
      .filter(item => item.concept !== "pizza")
      .reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // Get pizza addons total (extra cheese etc) - always charged
    const pizzaAddonsTotal = items
      .filter(item => item.concept === "pizza" && item.category === "pizza_addon")
      .reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    const subtotal = nonPizzaTotal + pizzaPricing.pizzaTotal + pizzaAddonsTotal;
    
    // Apply promo discount
    const promoDiscount = appliedPromo?.discount_amount || 0;

    // Phase 14 Batch C: project loyalty reward (client-side estimate; server is source of truth)
    let projectedRewards = 0;
    let loyaltyDiscount = 0;
    if (loyalty.enabled && pizzaPricing.totalPizzaUnits > 0) {
      const target = loyalty.target || 6;
      const combined = (loyalty.count || 0) + pizzaPricing.totalPizzaUnits;
      // Every (target+1) pizzas earns one free; simple math: rewards = floor(count_after / (target+1)) approx.
      // Match backend: rewards_earned = number of times (count_after > target) triggers.
      let count = loyalty.count || 0;
      count += pizzaPricing.totalPizzaUnits;
      while (count > target) {
        projectedRewards += 1;
        count -= (target + 1);
        if (count < 0) count = 0;
      }
      if (projectedRewards > 0) {
        // Discount = cheapest pizza price × number of rewards (capped to available units)
        loyaltyDiscount = Math.min(projectedRewards, pizzaPricing.totalPizzaUnits) * (pizzaPricing.minPizzaPrice || 0);
        loyaltyDiscount = Math.round(loyaltyDiscount * 100) / 100;
      }
      // Reference `combined` to satisfy linter — kept for future debugging
      void combined;
    }

    const discountAmount = promoDiscount + loyaltyDiscount;
    const subtotalAfterDiscount = Math.max(0, subtotal - discountAmount);
    
    // Phase 14: consolidated Service Fee = 3% + $0.30 (was Processing + Service).
    // Only charged when paying online.
    let serviceFee = 0;
    if (paymentMethod === "online" && paymentsEnabled) {
      serviceFee = Math.round((subtotalAfterDiscount * STRIPE_FEES.processingPercent + STRIPE_FEES.serviceFee) * 100) / 100;
    }
    
    // Subtotal with fees (for tax calculation)
    const subtotalWithFees = subtotalAfterDiscount + serviceFee;
    
    // Tax is calculated on subtotal WITH fees
    const taxAmount = Math.round(subtotalWithFees * TAX_RATE * 100) / 100;
    const grandTotal = Math.round((subtotalWithFees + taxAmount) * 100) / 100;
    
    return { subtotal, discountAmount, loyaltyDiscount, subtotalAfterDiscount, serviceFee, subtotalWithFees, taxAmount, grandTotal, projectedRewards };
  }, [items, pizzaPricing, appliedPromo, paymentMethod, paymentsEnabled, loyalty]);
  
  // For backwards compatibility
  const adjustedTotal = grandTotal;

  // Apply promo code handler
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError("");
    
    try {
      const response = await axios.post(`${API}/promo-codes/apply`, {
        code: promoCode.trim().toUpperCase(),
        items: items.map(item => ({
          name: item.name,
          concept: item.concept,
          category: item.category,
          price: item.price,
          quantity: item.quantity,
        })),
        subtotal: subtotal,
      });
      
      setAppliedPromo({
        code: response.data.code,
        discount_amount: response.data.discount_amount,
        message: response.data.message,
      });
      toast.success(response.data.message);
      setPromoCode("");
    } catch (e) {
      const errorMsg = e.response?.data?.detail || "Invalid promo code";
      setPromoError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoError("");
  };

  const handleGuestCheckout = async () => {
    if (!customerName.trim()) { toast.error("Please enter your name"); return; }
    if (!customerEmail.trim()) { toast.error("Please enter your email"); return; }
    if (!phoneNumber.trim()) { toast.error("Please enter your phone number"); return; }
    
    try {
      await guestCheckout(customerName.trim(), customerEmail.trim(), phoneNumber.trim());
      setCheckoutMode('authenticated');
      toast.success("Ready to place your order!");
    } catch (e) {
      toast.error("Failed to continue. Please try again.");
    }
  };

  const handleSubmitOrder = async () => {
    if (!customerName.trim()) { toast.error("Please enter your name"); return; }
    if (!phoneNumber.trim()) { toast.error("Please enter your phone number"); return; }
    if (items.length === 0) return;

    setSubmitting(true);
    try {
      const orderItems = items.map((item) => ({
        name: item.name, concept: item.concept, category: item.category,
        price: item.price, quantity: item.quantity, customizations: item.customizations || {},
      }));
      const res = await axios.post(`${API}/orders`, {
        customer_name: customerName.trim(),
        phone_number: phoneNumber.trim(),
        notes: orderNotes.trim() || null,
        customer_email: customerEmail.trim() || null,
        items: orderItems,
        subtotal: subtotal,
        promo_code: appliedPromo?.code || null,
        discount_amount: discountAmount || null,
        subtotal_after_discount: subtotalAfterDiscount || null,
        processing_fee: null,
        service_fee: serviceFee || null,
        tax_rate: TAX_RATE,
        tax_amount: taxAmount,
        total: grandTotal,
        payment_method: paymentMethod,
        customer_id: customer?.customer_id || null,
      }, { withCredentials: true });
      
      const newOrderId = res.data.id;
      
      // If paying online, redirect to Stripe Checkout
      if (paymentMethod === "online" && paymentsEnabled) {
        setProcessingPayment(true);
        try {
          const paymentRes = await axios.post(`${API}/payments/create-session`, {
            order_id: newOrderId,
            origin_url: window.location.origin
          });
          
          // Clear cart before redirecting
          clearCart();
          setAppliedPromo(null);
          
          // Redirect to Stripe Checkout
          window.location.href = paymentRes.data.checkout_url;
          return;
        } catch (paymentError) {
          console.error("Payment session error:", paymentError);
          toast.error("Failed to initiate payment. Your order has been placed for pickup payment.");
          // Fall through to regular order success flow
        } finally {
          setProcessingPayment(false);
        }
      }
      
      // Regular order flow (pay at pickup)
      setOrderId(newOrderId);
      setOrderStatus("waiting");
      setDeclineReason(null);
      setOrderPlaced(true);
      clearCart();
      setAppliedPromo(null);
      toast.success("Order placed! Waiting for the store to confirm...");
      
      // Show push notification prompt if supported and not already granted
      if (pushService.isSupported() && !pushService.isEnabled() && !pushService.isDenied()) {
        setShowPushPrompt(true);
      }
    } catch (e) {
      toast.error("Failed to place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnablePush = async () => {
    try {
      await pushService.subscribe();
      toast.success("Notifications enabled! You'll be notified when your order is ready.");
    } catch (e) {
      console.error('Push subscription error:', e);
      toast.error("Could not enable notifications. You'll receive updates via email.");
    }
    setShowPushPrompt(false);
  };

  const handleCloseDrawer = () => {
    if (orderPlaced) { 
      setOrderPlaced(false); 
      setCustomerName(customer?.name || ""); 
      setPhoneNumber(customer?.phone || ""); 
      setCustomerEmail(customer?.email || ""); 
      setOrderId(null); 
      setOrderStatus(null);
      setDeclineReason(null);
      setShowPushPrompt(false);
      setCheckoutMode(isInStoreMode ? 'instore' : (isAuthenticated ? 'authenticated' : 'select'));
      setPaymentMethod(isInStoreMode ? "at_counter" : "pickup");
    }
    onClose();
  };

  // Render in-store checkout form (simplified)
  const renderInStoreForm = () => (
    <div className="px-6 py-4 space-y-4 bg-white border-t">
      {/* In-Store Mode Indicator */}
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-xl"
        style={{ backgroundColor: "#0066cc10", border: "1px solid #0066cc20" }}
      >
        <Store className="h-4 w-4" style={{ color: "#0066cc" }} />
        <span className="text-sm font-medium" style={{ color: "#0066cc" }}>In-Store Order</span>
      </div>

      {/* Optional Order Name */}
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: BRAND_COLORS.darkSlate }}>
          Order Name <span className="text-xs font-normal" style={{ color: BRAND_COLORS.darkSlate + "66" }}>(optional)</span>
        </label>
        <Input 
          data-testid="instore-order-name"
          placeholder="e.g., Table 3, John, or leave blank" 
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          className="rounded-xl h-12 bg-slate-50 border-slate-200 text-sm" 
        />
      </div>

      {/* Optional Phone */}
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: BRAND_COLORS.darkSlate }}>
          Phone <span className="text-xs font-normal" style={{ color: BRAND_COLORS.darkSlate + "66" }}>(for callback, optional)</span>
        </label>
        <Input 
          data-testid="instore-phone"
          placeholder="555-123-4567" 
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          className="rounded-xl h-12 bg-slate-50 border-slate-200 text-sm" 
          type="tel"
        />
      </div>

      {/* Payment Info */}
      <div 
        className="flex items-center justify-between px-3 py-2 rounded-xl"
        style={{ backgroundColor: BRAND_COLORS.sandWhite }}
      >
        <span className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Payment:</span>
        <span className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>At Counter (Cash/Card)</span>
      </div>

      <Button 
        data-testid="instore-place-order-btn"
        className="w-full h-14 rounded-2xl font-bold text-base text-white"
        style={{ backgroundColor: "#0066cc" }}
        disabled={submitting}
        onClick={handleInStoreSubmit}
      >
        {submitting ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Placing Order...
          </span>
        ) : (
          `Complete Order - ${formatPrice(grandTotal)}`
        )}
      </Button>
    </div>
  );

  // Handle in-store order submission (simplified - no name/phone required)
  const handleInStoreSubmit = async () => {
    if (items.length === 0) return;

    setSubmitting(true);
    try {
      const orderItems = items.map((item) => ({
        name: item.name, concept: item.concept, category: item.category,
        price: item.price, quantity: item.quantity, customizations: item.customizations || {},
      }));
      
      // Get employee session
      const employeeSession = sessionStorage.getItem("employeeSession");
      const employeeName = employeeSession ? JSON.parse(employeeSession).name : "Employee";
      
      const res = await axios.post(`${API}/orders`, {
        customer_name: customerName.trim() || "In-Store Order",
        phone_number: phoneNumber.trim() || "",
        customer_email: null,
        items: orderItems,
        subtotal: subtotal,
        promo_code: appliedPromo?.code || null,
        discount_amount: discountAmount || null,
        subtotal_after_discount: subtotalAfterDiscount || null,
        processing_fee: null,
        service_fee: null,
        tax_rate: TAX_RATE,
        tax_amount: taxAmount,
        total: grandTotal,
        payment_method: "at_counter",
        order_mode: "instore",
        created_by: employeeName,
        customer_id: null,
      }, { withCredentials: true });
      
      setOrderId(res.data.id);
      setOrderPlaced(true);
      clearCart();
      setAppliedPromo(null);
      toast.success("Order placed! Ready for preparation.");
    } catch (e) {
      toast.error("Failed to place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Render checkout options (Google Sign-In or Guest)
  const renderCheckoutOptions = () => (
    <div className="px-6 py-4 space-y-4 bg-white border-t">
      <div className="text-center">
        <p className="text-sm font-semibold mb-1" style={{ color: BRAND_COLORS.darkSlate }}>
          How would you like to checkout?
        </p>
        <p className="text-xs" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
          Sign in to save your order history
        </p>
      </div>
      
      {/* Google Sign-In Button */}
      <Button
        data-testid="google-signin-btn"
        className="w-full h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-3"
        style={{ backgroundColor: "white", border: `2px solid ${BRAND_COLORS.deepTeal}`, color: BRAND_COLORS.deepTeal }}
        onClick={loginWithGoogle}
        disabled={processingAuth}
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        {processingAuth ? "Signing in..." : "Sign in with Google"}
      </Button>
      
      {/* Guest Checkout Button */}
      <Button
        data-testid="guest-checkout-btn"
        variant="outline"
        className="w-full h-12 rounded-xl font-semibold text-sm"
        style={{ borderColor: BRAND_COLORS.darkSlate + "30" }}
        onClick={() => setCheckoutMode('guest')}
      >
        <User className="h-4 w-4 mr-2" />
        Continue as Guest
      </Button>
      
      <p className="text-[10px] text-center" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
        Guest checkout requires name, email & phone for order updates
      </p>
    </div>
  );

  // Render guest checkout form
  const renderGuestForm = () => (
    <div className="border-t px-6 py-4 space-y-3 bg-white">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>Guest Checkout</p>
        <button 
          className="text-xs underline" 
          style={{ color: BRAND_COLORS.tropicalAqua }}
          onClick={() => setCheckoutMode('select')}
        >
          Back to options
        </button>
      </div>
      
      <div className="relative">
        <User className="absolute left-3 top-3.5 h-4 w-4" style={{ color: BRAND_COLORS.darkSlate + "66" }} />
        <Input data-testid="guest-name-input" placeholder="Your name *" value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          className="rounded-xl h-12 bg-slate-50 border-slate-200 text-sm pl-10" />
      </div>
      <div className="relative">
        <Mail className="absolute left-3 top-3.5 h-4 w-4" style={{ color: BRAND_COLORS.darkSlate + "66" }} />
        <Input data-testid="guest-email-input" placeholder="Email *" value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value)}
          className="rounded-xl h-12 bg-slate-50 border-slate-200 text-sm pl-10" type="email" />
      </div>
      <div className="relative">
        <Phone className="absolute left-3 top-3.5 h-4 w-4" style={{ color: BRAND_COLORS.darkSlate + "66" }} />
        <Input data-testid="guest-phone-input" placeholder="Phone number *" value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          className="rounded-xl h-12 bg-slate-50 border-slate-200 text-sm pl-10" type="tel" />
      </div>
      
      <Button 
        data-testid="continue-guest-btn"
        className="w-full h-12 rounded-xl font-semibold text-sm text-white"
        style={{ backgroundColor: BRAND_COLORS.tropicalAqua }}
        onClick={handleGuestCheckout}
        disabled={!customerName.trim() || !customerEmail.trim() || !phoneNumber.trim()}
      >
        Continue to Order
      </Button>
    </div>
  );

  // Render authenticated checkout form
  const renderAuthenticatedForm = () => (
    <div className="border-t px-6 py-4 space-y-3 bg-white">
      {/* Pizza Savings Banner */}
      {pizzaPricing.hasSavings && (
        <div className="p-3 rounded-xl flex items-center gap-2" style={{ backgroundColor: BRAND_COLORS.deepTeal + "10" }}>
          <Tag className="h-4 w-4" style={{ color: BRAND_COLORS.deepTeal }} />
          <div className="flex-1">
            <p className="text-xs font-semibold" style={{ color: BRAND_COLORS.deepTeal }}>
              Classic Pizza Deal! 🍕
            </p>
            <p className="text-xs" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
              {pizzaPricing.totalPizzas} classic pizza{pizzaPricing.totalPizzas > 1 ? 's' : ''}: 1st $11.99, 2nd $10.99 alternating = Save {formatPrice(pizzaPricing.savings)}!
            </p>
          </div>
        </div>
      )}

      {/* Price Breakdown */}
      {/* Promo Code Section */}
      <div className="space-y-2">
        {appliedPromo ? (
          <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: "#10B98110" }}>
            <div className="flex items-center gap-2">
              <Ticket className="h-4 w-4" style={{ color: "#10B981" }} />
              <div>
                <p className="text-xs font-bold" style={{ color: "#10B981" }}>
                  {appliedPromo.code} Applied!
                </p>
                <p className="text-[10px]" style={{ color: "#10B98199" }}>
                  You saved {formatPrice(appliedPromo.discount_amount)}
                </p>
              </div>
            </div>
            <button 
              onClick={handleRemovePromo}
              className="p-1 rounded-full hover:bg-red-100"
            >
              <X className="h-4 w-4 text-red-500" />
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Ticket className="absolute left-3 top-2.5 h-4 w-4" style={{ color: BRAND_COLORS.darkSlate + "66" }} />
              <Input
                data-testid="promo-code-input"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }}
                className="rounded-xl h-10 bg-white border-slate-200 text-sm pl-10 uppercase"
                onKeyDown={(e) => e.key === "Enter" && handleApplyPromo()}
              />
            </div>
            <Button
              data-testid="apply-promo-btn"
              variant="outline"
              className="h-10 rounded-xl font-semibold text-xs px-4"
              style={{ borderColor: BRAND_COLORS.deepTeal, color: BRAND_COLORS.deepTeal }}
              onClick={handleApplyPromo}
              disabled={promoLoading || !promoCode.trim()}
            >
              {promoLoading ? "..." : "Apply"}
            </Button>
          </div>
        )}
        {promoError && (
          <p className="text-xs text-red-500 pl-1">{promoError}</p>
        )}
      </div>

      {/* Pizza Loyalty Progress - Phase 14 Batch C */}
      {loyalty.enabled && (loyalty.count > 0 || (pizzaPricing.totalPizzas + pizzaPricing.specialtyCount) > 0) && (() => {
        const target = loyalty.target || 6;
        const pizzasInCart = pizzaPricing.totalPizzas + pizzaPricing.specialtyCount;
        const projected = Math.min(loyalty.count + pizzasInCart, target + 1);
        const willEarn = loyalty.count + pizzasInCart > target;
        const remaining = Math.max(target - loyalty.count, 0);
        return (
          <div
            data-testid="loyalty-progress"
            className="rounded-xl px-4 py-3 border"
            style={{
              backgroundColor: willEarn ? "#F0FDF4" : "#FFFBEB",
              borderColor: willEarn ? "#86EFAC" : "#FDE68A",
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-base">{willEarn ? "🎉" : "🍕"}</span>
                <span className="text-sm font-bold" style={{ color: willEarn ? "#166534" : "#78350F" }}>
                  {willEarn
                    ? "You've earned a FREE pizza!"
                    : remaining === 0
                      ? "Your next pizza is FREE!"
                      : `${remaining} more pizza${remaining === 1 ? "" : "s"} to earn a FREE one`}
                </span>
              </div>
              <span className="text-xs font-mono font-semibold" style={{ color: willEarn ? "#166534" : "#78350F" }}>
                {loyalty.count}/{target}
              </span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: "#E5E7EB" }}>
              <div
                className="h-full transition-all rounded-full"
                style={{
                  width: `${Math.min((projected / target) * 100, 100)}%`,
                  backgroundColor: willEarn ? "#22C55E" : "#F59E0B",
                }}
              />
            </div>
            <p className="text-[10px] mt-1.5" style={{ color: (willEarn ? "#166534" : "#78350F") + "AA" }}>
              Buy {target}, get the {target + 1}th pizza free. Counter resets on the 1st of each month.
            </p>
          </div>
        );
      })()}

      {/* Order Notes - Phase 14 */}
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: BRAND_COLORS.darkSlate }}>
          Special Instructions <span className="text-xs font-normal" style={{ color: BRAND_COLORS.darkSlate + "66" }}>(optional)</span>
        </label>
        <textarea
          data-testid="order-notes"
          value={orderNotes}
          onChange={(e) => setOrderNotes(e.target.value.slice(0, 250))}
          placeholder="Allergies, prep preferences, pickup instructions..."
          rows={2}
          maxLength={250}
          className="w-full rounded-xl bg-slate-50 border border-slate-200 text-sm px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-slate-400"
        />
        <div className="text-right text-[11px] mt-0.5" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
          {orderNotes.length}/250
        </div>
      </div>

      {/* Price Breakdown */}
      <div className="space-y-2 p-3 rounded-xl" style={{ backgroundColor: BRAND_COLORS.sandWhite }}>
        {/* Subtotal */}
        <div className="flex items-center justify-between">
          <span className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Subtotal</span>
          <div className="text-right">
            {pizzaPricing.hasSavings && (
              <span className="text-sm line-through mr-2" style={{ color: BRAND_COLORS.darkSlate + "50" }}>
                {formatPrice(total)}
              </span>
            )}
            <span className="text-sm font-medium" style={{ color: BRAND_COLORS.darkSlate }}>
              {formatPrice(subtotal)}
            </span>
          </div>
        </div>
        
        {/* Discount (if promo applied) */}
        {appliedPromo && (discountAmount - loyaltyDiscount) > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: "#10B981" }}>
              Promo ({appliedPromo.code})
            </span>
            <span className="text-sm font-medium" style={{ color: "#10B981" }}>
              -{formatPrice(discountAmount - loyaltyDiscount)}
            </span>
          </div>
        )}

        {/* Loyalty Reward Discount */}
        {loyaltyDiscount > 0 && (
          <div className="flex items-center justify-between" data-testid="loyalty-discount-line">
            <span className="text-sm font-semibold" style={{ color: "#22C55E" }}>
              🎉 Free Pizza Reward{projectedRewards > 1 ? ` × ${projectedRewards}` : ""}
            </span>
            <span className="text-sm font-bold" style={{ color: "#22C55E" }}>
              -{formatPrice(loyaltyDiscount)}
            </span>
          </div>
        )}
        
        {/* Service Fee - Only show when Pay Online selected (was previously split into Processing + Service) */}
        {paymentMethod === "online" && paymentsEnabled && (
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "80" }}>
              Service Fee
            </span>
            <span className="text-sm font-medium" style={{ color: BRAND_COLORS.darkSlate + "80" }}>
              +{formatPrice(serviceFee)}
            </span>
          </div>
        )}
        
        {/* Tax */}
        <div className="flex items-center justify-between">
          <span className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Tax (10%)</span>
          <span className="text-sm font-medium" style={{ color: BRAND_COLORS.darkSlate }}>
            {formatPrice(taxAmount)}
          </span>
        </div>
        
        <Separator className="my-2" />
        
        {/* Grand Total */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold" style={{ color: BRAND_COLORS.darkSlate }}>Total</span>
          <span className="text-xl font-extrabold" style={{ color: BRAND_COLORS.sunsetOrange, fontFamily: "Montserrat" }}>
            {formatPrice(grandTotal)}
          </span>
        </div>
      </div>
      
      {/* Show logged-in user info */}
      {isAuthenticated && customer && (
        <div className="p-3 rounded-xl flex items-center gap-3" style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "10" }}>
          {customer.picture ? (
            <img src={customer.picture} alt="" className="h-8 w-8 rounded-full" />
          ) : (
            <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND_COLORS.tropicalAqua }}>
              <User className="h-4 w-4 text-white" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: BRAND_COLORS.darkSlate }}>{customer.name}</p>
            <p className="text-xs truncate" style={{ color: BRAND_COLORS.darkSlate + "99" }}>{customer.email}</p>
          </div>
        </div>
      )}
      
      <Input data-testid="customer-name-input" placeholder="Your name *" value={customerName}
        onChange={(e) => setCustomerName(e.target.value)}
        className="rounded-xl h-12 bg-slate-50 border-slate-200 text-sm" />
      <div className="relative">
        <Phone className="absolute left-3 top-3.5 h-4 w-4" style={{ color: BRAND_COLORS.darkSlate + "66" }} />
        <Input data-testid="phone-number-input" placeholder="Phone number *" value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          className="rounded-xl h-12 bg-slate-50 border-slate-200 text-sm pl-10" type="tel" />
      </div>
      {!isAuthenticated && (
        <Input data-testid="customer-email-input" placeholder="Email (for order updates)" value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value)}
          className="rounded-xl h-12 bg-slate-50 border-slate-200 text-sm" type="email" />
      )}
      
      {/* Payment Method Selection - Show based on admin toggles */}
      {(paymentsEnabled || storePaymentEnabled) && (
        <div className="space-y-2">
          <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>Payment Method</p>
          <div className={`grid gap-2 ${paymentsEnabled && storePaymentEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {storePaymentEnabled && (
              <button
                data-testid="payment-pickup"
                className="p-3 rounded-xl border-2 text-left transition-all flex flex-col items-center gap-1"
                style={{
                  borderColor: paymentMethod === "pickup" ? BRAND_COLORS.deepTeal : "#E5E7EB",
                  backgroundColor: paymentMethod === "pickup" ? BRAND_COLORS.deepTeal + "10" : "white"
                }}
                onClick={() => setPaymentMethod("pickup")}
              >
                <Store className="h-5 w-5" style={{ color: paymentMethod === "pickup" ? BRAND_COLORS.deepTeal : BRAND_COLORS.darkSlate + "66" }} />
                <span className="text-xs font-medium" style={{ color: BRAND_COLORS.darkSlate }}>Pay at Pickup</span>
              </button>
            )}
            {paymentsEnabled && (
              <button
                data-testid="payment-online"
                className="p-3 rounded-xl border-2 text-left transition-all flex flex-col items-center gap-1"
                style={{
                  borderColor: paymentMethod === "online" ? BRAND_COLORS.tropicalAqua : "#E5E7EB",
                  backgroundColor: paymentMethod === "online" ? BRAND_COLORS.tropicalAqua + "10" : "white"
                }}
                onClick={() => setPaymentMethod("online")}
              >
                <CreditCard className="h-5 w-5" style={{ color: paymentMethod === "online" ? BRAND_COLORS.tropicalAqua : BRAND_COLORS.darkSlate + "66" }} />
                <span className="text-xs font-medium" style={{ color: BRAND_COLORS.darkSlate }}>Pay Online</span>
              </button>
            )}
          </div>
          {paymentMethod === "online" && (
            <p className="text-xs text-center" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
              You&apos;ll be redirected to secure checkout
            </p>
          )}
        </div>
      )}

      <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-4 px-4 pt-3 pb-4 mt-2" style={{ boxShadow: "0 -8px 12px -8px rgba(0,0,0,0.05)" }}>
        <Button data-testid="place-order-btn" className="w-full h-14 rounded-2xl font-bold text-base text-white"
          style={{ backgroundColor: paymentMethod === "online" ? BRAND_COLORS.tropicalAqua : BRAND_COLORS.sunsetOrange }}
          disabled={submitting || processingPayment || !customerName.trim() || !phoneNumber.trim()}
          onClick={handleSubmitOrder}>
          {submitting || processingPayment ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              {processingPayment ? "Redirecting to Payment..." : "Placing Order..."}
            </span>
          ) : paymentMethod === "online" && paymentsEnabled ? (
            `Pay Online - ${formatPrice(grandTotal)}`
          ) : (
            `Place Order - ${formatPrice(grandTotal)}`
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleCloseDrawer()}>
      <SheetContent className="flex flex-col w-full sm:max-w-md p-0" style={{ backgroundColor: BRAND_COLORS.sandWhite }}>
        <SheetHeader className="px-6 pt-6 pb-4" style={{ backgroundColor: BRAND_COLORS.deepTeal }}>
          <SheetTitle style={{ fontFamily: "Montserrat" }} className="flex items-center gap-2 text-white">
            <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-full object-cover" />
            {orderPlaced
              ? (orderStatus === "declined"
                  ? "Order Declined"
                  : orderStatus === "waiting"
                    ? "Confirming Order..."
                    : "Order Confirmed")
              : "Your Cart"}
          </SheetTitle>
          <div className="flex items-center gap-2 text-white/80 text-sm">
            {orderPlaced ? (
              <span>
                {orderStatus === "declined"
                  ? "The store was unable to accept your order."
                  : orderStatus === "waiting"
                    ? "Waiting for the store to accept..."
                    : "Your order is being prepared!"}
              </span>
            ) : (
              <>
                <Badge style={{ backgroundColor: BRAND_COLORS.tropicalAqua }} className="text-white text-[10px]">TO-GO</Badge>
                <span>{itemCount} item{itemCount !== 1 ? "s" : ""}</span>
              </>
            )}
          </div>
        </SheetHeader>

        {orderPlaced && orderStatus === "waiting" ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-testid="waiting-for-acceptance">
            {/* Animated pulsing loader */}
            <div className="relative mb-6">
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center"
                style={{ backgroundColor: BRAND_COLORS.sunsetOrange + "18" }}
              >
                <Loader2 className="h-12 w-12 animate-spin" style={{ color: BRAND_COLORS.sunsetOrange }} />
              </div>
              <div
                className="absolute inset-0 rounded-full animate-ping"
                style={{ backgroundColor: BRAND_COLORS.sunsetOrange + "22" }}
              />
            </div>

            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
              The store is reviewing your order
            </h3>
            <p className="text-sm mb-1" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
              We&apos;re checking with the kitchen. This usually takes under a minute.
            </p>
            <p className="text-xs font-mono mb-6" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
              Order ID: {orderId?.slice(0, 8)}
            </p>

            <div className="w-full rounded-2xl p-4 border" style={{ backgroundColor: "white", borderColor: BRAND_COLORS.tropicalAqua + "40" }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "18" }}>
                  <Clock className="h-5 w-5" style={{ color: BRAND_COLORS.tropicalAqua }} />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>Hang tight, {customerName || "there"}!</p>
                  <p className="text-xs" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
                    You&apos;ll see the confirmation as soon as it&apos;s accepted.
                  </p>
                </div>
              </div>
            </div>

            <Link to={`/order/status/${orderId}`} className="block w-full mt-4">
              <Button
                data-testid="track-order-btn"
                variant="outline"
                className="w-full rounded-xl h-11 px-6 font-semibold text-sm"
                style={{ borderColor: BRAND_COLORS.tropicalAqua, color: BRAND_COLORS.deepTeal }}
              >
                Track Order Status
              </Button>
            </Link>

            <Link to={`/order/status/${orderId}`} className="block w-full mt-2">
              <Button
                data-testid="chat-store-waiting-btn"
                className="w-full rounded-xl h-11 px-6 font-semibold text-sm text-white"
                style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
              >
                💬 Chat with Store
              </Button>
            </Link>

            {/* Fun Facts to keep the customer entertained while the store confirms */}
            <div className="w-full mt-4">
              <FunFactsCarousel intervalMs={6000} />
            </div>
          </div>
        ) : orderPlaced && orderStatus === "declined" ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center" data-testid="order-declined">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: "#FEE2E2" }}>
              <X className="h-8 w-8" style={{ color: "#DC2626" }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
              Sorry, {customerName}!
            </h3>
            <p className="text-sm mb-2" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
              The store couldn&apos;t accept your order right now.
            </p>
            {declineReason && (
              <div className="w-full rounded-xl p-3 mb-4 border" style={{ backgroundColor: "#FEF2F2", borderColor: "#FECACA" }}>
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: "#991B1B" }}>Reason</p>
                <p className="text-sm" style={{ color: "#7F1D1D" }}>{declineReason}</p>
              </div>
            )}
            <p className="text-xs font-mono mb-6" style={{ color: BRAND_COLORS.darkSlate + "66" }}>Order ID: {orderId?.slice(0, 8)}</p>
            <p className="text-xs mb-6" style={{ color: BRAND_COLORS.darkSlate + "99" }}>
              You have not been charged. Please give us a call if you have any questions.
            </p>
            <Button
              data-testid="new-order-btn"
              className="w-full rounded-xl h-12 px-8 font-bold text-white"
              style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
              onClick={handleCloseDrawer}
            >
              Try Again
            </Button>
          </div>
        ) : orderPlaced ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            {/* Success Icon */}
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: BRAND_COLORS.tropicalAqua + "20" }}>
              <CheckCircle className="h-8 w-8" style={{ color: BRAND_COLORS.tropicalAqua }} />
            </div>
            
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
              Thank You, {customerName}!
            </h3>
            <p className="text-sm mb-1" style={{ color: BRAND_COLORS.darkSlate + "99" }}>Your pick-up order has been sent to the kitchen.</p>
            <p className="text-xs font-mono mb-6" style={{ color: BRAND_COLORS.darkSlate + "66" }}>Order ID: {orderId?.slice(0, 8)}</p>
            
            {/* Restaurant Location Card */}
            <div 
              className="w-full rounded-2xl p-5 mb-4 border"
              style={{ 
                backgroundColor: "white", 
                borderColor: BRAND_COLORS.tropicalAqua + "40"
              }}
            >
              {/* Logo */}
              <img 
                src={LOGO_URL} 
                alt="Dauphin Island Food Mart" 
                className="w-20 h-20 rounded-xl object-cover mx-auto mb-3 shadow-sm"
              />
              
              <p className="text-sm font-bold mb-3" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
                Pickup Location
              </p>
              
              {/* Address with Map Link */}
              <a 
                href="https://maps.google.com/?q=1000+Bienville+Blvd,+Dauphin+Island,+AL+36528"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 p-3 rounded-xl mb-3 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ backgroundColor: BRAND_COLORS.deepTeal + "10" }}
                data-testid="maps-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" style={{ color: BRAND_COLORS.deepTeal }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span className="text-sm font-medium" style={{ color: BRAND_COLORS.deepTeal }}>
                  1000 Bienville Blvd, Dauphin Island, AL 36528
                </span>
              </a>
              
              {/* Phone */}
              <a 
                href="tel:+12518612320"
                className="flex items-center justify-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: BRAND_COLORS.sunsetOrange }}
                data-testid="phone-link"
              >
                <Phone className="h-4 w-4" />
                (251) 861-2320
              </a>
            </div>
            
            <Link to={`/order/status/${orderId}`} className="block w-full">
              <Button data-testid="track-order-btn" variant="outline" className="w-full rounded-xl h-10 px-6 font-semibold text-sm"
                style={{ borderColor: BRAND_COLORS.tropicalAqua, color: BRAND_COLORS.deepTeal }}>
                Track Order Status
              </Button>
            </Link>
            <Button data-testid="new-order-btn" className="mt-3 w-full rounded-xl h-12 px-8 font-bold text-white"
              style={{ backgroundColor: BRAND_COLORS.sunsetOrange }} onClick={handleCloseDrawer}>
              Place Another Order
            </Button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto min-h-0" data-testid="cart-scroll-area">
            <div className="px-6">
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-3" style={{ color: BRAND_COLORS.darkSlate + "30" }} />
                  <p className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "66" }}>Your cart is empty</p>
                </div>
              ) : (
                <div className="space-y-3 pb-4 pt-4">
                  {items.map((item) => (
                    <div key={item.cartId} data-testid={`cart-item-${item.cartId}`} className="bg-white rounded-xl p-3 shadow-sm">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate" style={{ color: BRAND_COLORS.darkSlate }}>{item.name}</p>
                          {Object.entries(item.customizations || {})
                            .filter(([key, val]) => {
                              // Hide internal/technical flags that shouldn't be surfaced to shoppers.
                              const INTERNAL_KEYS = new Set([
                                "isSpecialty",
                                "isLimitedEdition",
                                "isPremium",
                                "isCombo",
                                "concept",
                                "category",
                              ]);
                              if (INTERNAL_KEYS.has(key)) return false;
                              return val !== null && val !== undefined && val !== "" && val !== false;
                            })
                            .map(([key, val]) => (
                              <p key={key} className="text-xs mt-0.5" style={{ color: BRAND_COLORS.darkSlate + "99" }}>{key}: {val}</p>
                            ))
                          }
                        </div>
                        <button data-testid={`remove-item-${item.cartId}`}
                          className="p-1 transition-colors hover:opacity-70"
                          style={{ color: BRAND_COLORS.darkSlate + "66" }}
                          onClick={() => removeItem(item.cartId)}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1 bg-slate-100 rounded-full px-1 py-0.5">
                          <button
                            data-testid="qty-minus"
                            aria-label="Decrease quantity"
                            onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                            className="h-6 w-6 rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors"
                          >
                            <Minus className="h-3 w-3" style={{ color: BRAND_COLORS.darkSlate }} />
                          </button>
                          <span className="text-sm font-semibold min-w-[20px] text-center">{item.quantity}</span>
                          <button
                            data-testid="qty-plus"
                            aria-label="Increase quantity"
                            onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                            className="h-6 w-6 rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors"
                          >
                            <Plus className="h-3 w-3" style={{ color: BRAND_COLORS.darkSlate }} />
                          </button>
                        </div>
                        <span className="font-bold text-sm" style={{ color: BRAND_COLORS.sunsetOrange }}>{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <>
                {/* Show checkout options or form based on mode */}
                {checkoutMode === 'select' && renderCheckoutOptions()}
                {checkoutMode === 'guest' && renderGuestForm()}
                {checkoutMode === 'authenticated' && renderAuthenticatedForm()}
                {checkoutMode === 'instore' && renderInStoreForm()}
              </>
            )}

            {/* Push Notification Prompt */}
            {showPushPrompt && (
              <div className="px-6 py-4 bg-blue-50 border-t border-blue-100">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🔔</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-800">Get real-time updates!</p>
                    <p className="text-xs text-blue-600 mt-1">Allow notifications to know when your food is ready.</p>
                    <div className="flex gap-2 mt-3">
                      <Button 
                        size="sm" 
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                        onClick={handleEnablePush}
                      >
                        Enable
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-blue-600"
                        onClick={() => setShowPushPrompt(false)}
                      >
                        Not now
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
